import type { DeviceDetail } from '@irrigazione/shared';
import { Notice } from './ui';
import {
  formatAgo,
  formatCommandState,
  formatDeliveryMs,
  formatDuration,
  formatNumber,
  formatOutcome,
  formatTrigger,
  formatWhen,
} from '../format';

/**
 * Stato corrente dei sensori. Raw e calibrato sono affiancati di proposito:
 * quando una lettura sembra sbagliata, il confronto dice subito se il problema
 * e' il sensore o la calibrazione.
 */
export function StatusTab({ device }: { device: DeviceDetail }) {
  const reading = device.lastReading;

  const rows = [
    {
      sensor: 'Umidità terreno',
      raw: reading?.moistureRaw,
      value: reading?.moisturePct == null ? '—' : `${reading.moisturePct.toFixed(1)} %`,
    },
    {
      sensor: 'Batteria',
      raw: reading?.batteryRaw,
      value:
        reading?.batteryPct == null
          ? '—'
          : `${reading.batteryPct.toFixed(1)} % · ${formatNumber((reading.batteryMv ?? 0) / 1000, 'V', 2)}`,
    },
    ...(device.type === 'standalone'
      ? [
          {
            sensor: 'Livello serbatoio',
            raw: reading?.waterLevelRaw,
            value:
              reading?.waterLevelPct == null
                ? '—'
                : `${reading.waterLevelPct.toFixed(1)} % · ${formatNumber(reading.waterLevelCm, 'cm')}`,
          },
        ]
      : []),
  ];

  const activeCommands = device.commands.filter(
    (command) => command.state === 'pending' || command.state === 'sent',
  );
  /*
   * I comandi non consegnati vanno mostrati, non fatti sparire: prima la
   * tabella conteneva solo quelli in coda, quindi un «Irriga ora» che il
   * gateway non riusciva a far arrivare al nodo si limitava a svanire, senza
   * dire niente. Si guardano solo i piu' recenti, per non trasformare la
   * scheda in uno storico.
   */
  const failedCommands = device.commands
    .filter((command) => command.state === 'failed' || command.state === 'expired')
    .slice(0, 3);
  const lastFailed = failedCommands[0];
  const shownCommands = [...activeCommands, ...failedCommands];

  return (
    <div className="stack">
      <section className="card stack" style={{ gap: 12 }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>Sensori</h3>
        {reading ? (
          <>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Sensore</th>
                    <th>Valore calibrato</th>
                    <th>Valore raw</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.sensor}>
                      <td style={{ color: 'var(--text-primary)' }}>{row.sensor}</td>
                      <td className="num">{row.value}</td>
                      <td className="num muted">{row.raw ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="tiny muted" style={{ margin: 0 }}>
              Lettura di {formatWhen(reading.ts)} ({formatAgo(reading.ts)})
              {reading.rssi != null && ` · RSSI ${reading.rssi} dBm`}
              {reading.snr != null && ` · SNR ${reading.snr} dB`}
            </p>
          </>
        ) : (
          <Notice>Nessuna lettura ricevuta finora da questo irrigatore.</Notice>
        )}
      </section>

      {shownCommands.length > 0 && (
        <section className="card stack" style={{ gap: 10 }}>
          <h3 style={{ margin: 0, fontSize: 15 }}>
            {activeCommands.length > 0 ? 'Comandi in coda' : 'Ultimi comandi'}
          </h3>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Comando</th>
                  <th>Durata</th>
                  <th>Stato</th>
                  <th>Consegna</th>
                  <th>Creato</th>
                </tr>
              </thead>
              <tbody>
                {shownCommands.map((command) => {
                  const undelivered = command.state === 'failed' || command.state === 'expired';
                  return (
                    <tr key={command.id}>
                      <td style={{ color: 'var(--text-primary)' }}>
                        {command.type === 'irrigate' ? 'Irrigazione' : 'Stop'}
                      </td>
                      <td className="num">{command.type === 'irrigate' ? formatDuration(command.durationS) : '—'}</td>
                      <td style={undelivered ? { color: 'var(--status-critical)' } : undefined}>
                        {formatCommandState(command.state)}
                      </td>
                      <td
                        className="num muted"
                        title="Dal primo tentativo di consegna del gateway alla conferma del nodo"
                      >
                        {formatDeliveryMs(command.deliveryMs)}
                      </td>
                      <td>{formatWhen(command.createdAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {lastFailed && (
            <Notice variant="warning">
              <strong>
                {lastFailed.state === 'expired'
                  ? 'Comando scaduto prima di essere consegnato'
                  : 'Comando non consegnato al nodo'}
              </strong>{' '}
              ({formatAgo(lastFailed.createdAt)}). L&apos;irrigazione non è avvenuta. Il gateway
              riprova con un preambolo lungo e poi alla prima chiamata del nodo: se non arriva
              nemmeno così, il nodo è spento, fuori portata o la sua antenna è schermata. Il log
              del gateway dice quale dei due casi (`cmd: … mai consegnato`).
            </Notice>
          )}
        </section>
      )}

      <section className="card stack" style={{ gap: 10 }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>Ultime irrigazioni</h3>
        {device.recentEvents.length === 0 ? (
          <p className="small muted" style={{ margin: 0 }}>
            Nessuna irrigazione registrata.
          </p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Quando</th>
                  <th>Durata</th>
                  <th>Origine</th>
                  <th>Esito</th>
                  <th>Umidità all&apos;avvio</th>
                </tr>
              </thead>
              <tbody>
                {device.recentEvents.map((event) => (
                  <tr key={event.id}>
                    <td>{formatWhen(event.startedAt)}</td>
                    <td className="num">{formatDuration(event.durationS)}</td>
                    <td>{formatTrigger(event.trigger, device.type)}</td>
                    <td style={{ color: event.outcome.startsWith('aborted') ? 'var(--status-warning)' : undefined }}>
                      {formatOutcome(event.outcome)}
                    </td>
                    <td className="num">
                      {event.moisturePctAtStart == null ? '—' : `${event.moisturePctAtStart.toFixed(0)} %`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
